/* BattleControls — floating pill in the top-right of the arena.
 *
 * Provides:
 *   - 1× / 2× speed toggle (client pacing only, never affects outcome)
 *   - SKIP (jumps to the outcome screen immediately)
 *   - Sound ON/OFF
 *   - Exit → back to previous page
 *
 * Speed choice is persisted per-device so returning players don't have
 * to re-set their preference every battle.
 */
import { memo } from 'react';
import { Volume2, VolumeX, FastForward, SkipForward, X, Play, Hand } from 'lucide-react';

const SPEED_KEY = 'wq_battle_speed';
const SOUND_KEY = 'wq_battle_sound';

export function loadPref(k, fallback) {
  try {
    const v = localStorage.getItem(k);
    if (v == null) return fallback;
    return JSON.parse(v);
  } catch { return fallback; }
}
export function savePref(k, v) {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* silent */ }
}

export default memo(function BattleControls({ speed, onSpeed, soundOn, onSound, autoPlay, onAutoPlay, onSkip, onExit, disabled }) {
  return (
    <div className="absolute top-3 right-3 z-50 flex items-center gap-1.5" data-testid="battle-controls">
      <button
        type="button"
        onClick={() => onAutoPlay(!autoPlay)}
        data-testid="battle-autoplay-toggle"
        aria-label={autoPlay ? 'Auto-play on' : 'Manual tap mode'}
        title={autoPlay ? 'Auto-play — hands-off' : 'Manual tap — you drive'}
        className={`px-2.5 py-1.5 rounded-full text-[10px] uppercase tracking-widest border transition-colors flex items-center gap-1 ${
          autoPlay
            ? 'bg-emerald-400 text-black border-emerald-400'
            : 'bg-black/40 text-white/80 border-white/15 hover:bg-black/60'
        }`}
      >
        {autoPlay ? <Play className="w-3 h-3" strokeWidth={3} /> : <Hand className="w-3 h-3" strokeWidth={2.5} />}
        {autoPlay ? 'Auto' : 'Tap'}
      </button>
      <button
        type="button"
        onClick={() => { const next = speed === 2 ? 1 : 2; onSpeed(next); savePref(SPEED_KEY, next); }}
        data-testid="battle-speed-toggle"
        aria-label={`Battle speed ${speed}x`}
        className={`px-2.5 py-1.5 rounded-full text-[10px] uppercase tracking-widest border transition-colors ${
          speed === 2
            ? 'bg-amber-400 text-black border-amber-400'
            : 'bg-black/40 text-white/80 border-white/15 hover:bg-black/60'
        }`}
      >
        {speed}× {speed === 2 ? '' : '⇢'}
      </button>
      <button
        type="button"
        onClick={onSkip}
        disabled={disabled}
        data-testid="battle-skip"
        aria-label="Skip to result"
        className="px-2.5 py-1.5 rounded-full bg-black/40 border border-white/15 text-white/80 text-[10px] uppercase tracking-widest hover:bg-black/60 flex items-center gap-1 disabled:opacity-40"
      >
        <SkipForward className="w-3 h-3" /> Skip
      </button>
      <button
        type="button"
        onClick={() => { onSound(!soundOn); savePref(SOUND_KEY, !soundOn); }}
        data-testid="battle-sound-toggle"
        aria-label={soundOn ? 'Sound on' : 'Sound off'}
        className="p-1.5 rounded-full bg-black/40 border border-white/15 text-white/80 hover:bg-black/60"
      >
        {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
      </button>
      <button
        type="button"
        onClick={onExit}
        data-testid="battle-exit"
        aria-label="Exit battle"
        className="p-1.5 rounded-full bg-black/40 border border-white/15 text-white/80 hover:bg-black/60"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
});
